import { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const ContactSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    service: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams({
        name: formData.name,
        phone: formData.phone,
        service: formData.service,
        message: formData.message,
      }).toString();

      const response = await fetch('https://readdy.ai/api/form/d4s9lhjrvmocq14jmlf0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody,
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', phone: '', service: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="bg-brand-gray py-24 lg:py-32" ref={ref}>
      <div className="max-w-7xl mx-auto px-6 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Column 1 - Contact Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-white"
          >
            <h3 className="text-4xl font-black mb-8">Get In Touch</h3>
            
            <div className="space-y-6">
              <a href="tel:5743776776" className="block group cursor-pointer">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-brand-green rounded-full">
                    <i className="ri-phone-fill text-white text-xl"></i>
                  </div>
                  <div>
                    <p className="text-sm text-gray-400 mb-1">Call Us</p>
                    <p className="text-brand-green text-2xl font-bold group-hover:text-brand-green/80 transition-colors">(574) 377-6776</p>
                  </div>
                </div>
              </a>

              <a href="mailto:charlys4776@gmail.com" className="block group cursor-pointer">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-brand-green rounded-full">
                    <i className="ri-mail-fill text-white text-xl"></i>
                  </div>
                  <div>
                    <p className="text-sm text-gray-400 mb-1">Email</p>
                    <p className="text-white text-base group-hover:text-brand-green transition-colors break-all">charlys4776@gmail.com</p>
                  </div>
                </div>
              </a>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-brand-green rounded-full">
                  <i className="ri-map-pin-fill text-white text-xl"></i>
                </div>
                <div>
                  <p className="text-sm text-gray-400 mb-1">Address</p>
                  <p className="text-white text-base leading-relaxed">1905 Crescent Dr<br />Warsaw, IN 46580</p>
                </div>
              </div>

              <div className="pt-6 border-t border-white/10">
                <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">HOURS</p>
                <p className="text-white text-base mb-1">Monday to Friday</p>
                <p className="text-brand-green text-xl font-bold">7:00AM – 7:00PM</p>
              </div>
            </div>
          </motion.div>

          {/* Column 2 - Quick Contact Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-white"
          >
            <h3 className="text-4xl font-black mb-8">Request Quote</h3>
            
            <form onSubmit={handleSubmit} data-readdy-form id="contact-form" className="space-y-5">
              <div>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="Your Name"
                  className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-brand-green focus:ring-2 focus:ring-brand-green/20 outline-none transition-all text-sm"
                />
              </div>

              <div>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                  placeholder="Phone Number"
                  className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-brand-green focus:ring-2 focus:ring-brand-green/20 outline-none transition-all text-sm"
                />
              </div>

              <div>
                <select
                  name="service"
                  value={formData.service}
                  onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                  required
                  className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white focus:border-brand-green focus:ring-2 focus:ring-brand-green/20 outline-none transition-all text-sm cursor-pointer"
                >
                  <option value="" className="bg-brand-gray">Select Service</option>
                  <option value="Concrete" className="bg-brand-gray">Concrete</option>
                  <option value="Patio" className="bg-brand-gray">Patio</option>
                  <option value="Pavers" className="bg-brand-gray">Pavers</option>
                  <option value="Drywall" className="bg-brand-gray">Drywall</option>
                  <option value="Other" className="bg-brand-gray">Other</option>
                </select>
              </div>

              <div>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={(e) => {
                    if (e.target.value.length <= 500) {
                      setFormData({ ...formData, message: e.target.value });
                    }
                  }}
                  required
                  rows={4}
                  maxLength={500}
                  placeholder="Tell us about your project..."
                  className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-brand-green focus:ring-2 focus:ring-brand-green/20 outline-none transition-all resize-none text-sm"
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
              </div>

              {submitStatus === 'success' && (
                <div className="bg-green-500/20 text-green-300 px-4 py-3 rounded-lg text-sm">
                  Thank you! We'll contact you soon.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-500/20 text-red-300 px-4 py-3 rounded-lg text-sm">
                  Something went wrong. Please try again.
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-brand-green text-white py-4 rounded-full font-semibold hover:bg-brand-green/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </motion.div>

          {/* Column 3 - Credentials */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-white"
          >
            <h3 className="text-4xl font-black mb-8">Why Choose Us</h3>
            
            <div className="space-y-6">
              <div className="flex items-center gap-4 border border-white/20 rounded-xl p-5 hover:border-brand-green transition-colors">
                <div className="w-12 h-12 flex items-center justify-center">
                  <i className="ri-shield-check-fill text-brand-green text-3xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-lg mb-1">LLC Licensed</h4>
                  <p className="text-gray-400 text-sm">Fully certified and compliant</p>
                </div>
              </div>

              <div className="flex items-center gap-4 border border-white/20 rounded-xl p-5 hover:border-brand-green transition-colors">
                <div className="w-12 h-12 flex items-center justify-center">
                  <i className="ri-checkbox-circle-fill text-brand-green text-3xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-lg mb-1">Fully Insured</h4>
                  <p className="text-gray-400 text-sm">Complete protection coverage</p>
                </div>
              </div>

              <div className="flex items-center gap-4 border border-white/20 rounded-xl p-5 hover:border-brand-green transition-colors">
                <div className="w-12 h-12 flex items-center justify-center">
                  <i className="ri-star-fill text-brand-green text-3xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-lg mb-1">10+ Years Experience</h4>
                  <p className="text-gray-400 text-sm">Proven track record</p>
                </div>
              </div>

              <div className="flex items-center gap-4 border border-white/20 rounded-xl p-5 hover:border-brand-green transition-colors">
                <div className="w-12 h-12 flex items-center justify-center">
                  <i className="ri-award-fill text-brand-green text-3xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-lg mb-1">Quality Guaranteed</h4>
                  <p className="text-gray-400 text-sm">Excellence in every project</p>
                </div>
              </div>

              <div className="pt-6">
                <button
                  onClick={() => document.querySelector('#vapi-widget-floating-button')?.dispatchEvent(new Event('click'))}
                  className="w-full bg-white text-brand-gray px-6 py-4 rounded-full font-semibold hover:bg-gray-100 transition-all flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-customer-service-2-fill text-xl"></i>
                  <span>Talk to Us Now</span>
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;